import { useMemo, useRef, useEffect } from 'react';

import { EventTrackingAction, UserEvent } from '../declarations';
import { getDeployEnv } from '../internal/deployEnv';
import { useContextualData } from './ContextualDataContext';
import { mergeContextualData } from '../contextualData';
import { globalObservability } from '../global';
import { TrackingOptions } from '../declarations';
import {
  BaseJourneyEventWrapper,
  CategoryName,
  EventName,
  EventData,
} from '../declarations/JourneyEvent';

export type UseObservabilityFunctions<
  AppCategory = undefined,
  AppActions = undefined,
  AppEventWrapper extends BaseJourneyEventWrapper = BaseJourneyEventWrapper,
> = {
  trackEvent: (
    action: EventTrackingAction<AppActions>,
    label?: string,
    data?: { [k: string]: unknown },
    options?: TrackingOptions<AppCategory>,
  ) => void;
  trackUserEvent: (event: UserEvent) => void;
  trackJourneyEvent: <
    C extends CategoryName<AppEventWrapper>,
    E extends EventName<AppEventWrapper, C>,
  >(
    categoryName: C,
    eventName: E,
    eventData: EventData<AppEventWrapper, C, E>,
  ) => void;
  trackError: (
    label: string,
    error?: Error,
    data?: { [k: string]: unknown },
    options?: TrackingOptions<AppCategory>,
  ) => void;
};

/** Gives you access to Observability methods, with ContextualData from the context automatically added. */
export function useObservability<
  AppCategory extends string | undefined = undefined,
  AppActions extends string | undefined = undefined,
  AppJourneyEventWrapper extends BaseJourneyEventWrapper = BaseJourneyEventWrapper,
>(): UseObservabilityFunctions<AppCategory, AppActions, AppJourneyEventWrapper> {
  const env = getDeployEnv();

  const contextualData = useContextualData();

  // Store contextualData in a ref so we don't update the track functions when contextualData changes.
  // Previously, any useEffect that depended on a track function would get called again whenever
  // contextualData changed, which is not the behavior people were expecting.
  const contextualDataRef = useRef(contextualData);
  useEffect(() => {
    contextualDataRef.current = contextualData;
  }, [contextualData]);

  return useMemo(
    () => ({
      trackEvent: (
        action,
        label,
        data?: { [k: string]: unknown },
        options?: TrackingOptions<AppCategory>,
      ) => {
        if (!globalObservability.isInitialized()) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackEvent');
        }
        globalObservability.trackEvent(
          action,
          label,
          mergeContextualData(contextualDataRef.current, { data }),
          options,
        );
      },
      trackUserEvent: (event: UserEvent) => {
        if (!globalObservability.isInitialized()) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackUserEvent');
        }
        globalObservability.trackUserEvent(event, contextualDataRef.current);
      },
      trackJourneyEvent: <
        C extends CategoryName<AppJourneyEventWrapper>,
        E extends EventName<AppJourneyEventWrapper, C>,
      >(
        categoryName: C,
        eventName: E,
        eventData: EventData<AppJourneyEventWrapper, C, E>,
      ) => {
        if (!globalObservability.isInitialized()) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackJourneyEvent');
        }
        const eventWrapper: Pick<AppJourneyEventWrapper, 'category'> = {
          category: {
            $case: categoryName,
            [categoryName]: {
              event: {
                $case: eventName,
                [eventName]: eventData,
              },
            },
          },
        };
        globalObservability.trackJourneyEvent(eventWrapper, contextualDataRef.current);
      },
      trackError: (
        label,
        error,
        data?: { [k: string]: unknown },
        options?: TrackingOptions<AppCategory>,
      ) => {
        if (!globalObservability.isInitialized()) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackError');
        }
        globalObservability.trackError(
          label,
          error,
          mergeContextualData(contextualDataRef.current, { data }),
          options,
        );
      },
    }),
    [env],
  );
}
